import React from "react";
import { Row, Col, Form, InputGroup, Table } from "react-bootstrap";
import Scrollbars from "react-custom-scrollbars-2";

export const CartSummary = ({
    t,
    discountType,
    onDiscountTypeChange,
    discountValue,
    onDiscountValueChange,
    transportCost,
    onTransportCostChange,
    taxes,
    totals,
    getCurrencySymbol,
    disableScrollProps,
    posTaxType,
    pharmacyStateCode,
    patientStateCode,
    cart,
    itemTaxes
}) => {
    const isSameState =
        !patientStateCode ||
        !pharmacyStateCode ||
        pharmacyStateCode === patientStateCode;

    // Function to group items by tax percentage, distribute bill discount,
    // and recalculate taxes on taxableAmountAfterDiscount
    const getTaxBreakdown = () => {
        if (!cart || !itemTaxes) return [];

        const taxGroups = {};
        let totalTaxableBeforeDiscount = 0;

        // First pass: group by tax rate and sum total taxable
        cart.forEach(item => {
            const itemKey = `${item._id}-${item.selectedBatchId}`;
            const taxInfo = itemTaxes[itemKey];

            if (!taxInfo) return;

            const taxRate = taxInfo.taxPercentage || 0;
            const itemTaxableAmount = taxInfo.taxableAmount || 0;

            if (!taxGroups[taxRate]) {
                taxGroups[taxRate] = {
                    taxRate: taxRate,
                    taxableAmountBeforeDiscount: 0,
                    taxableAmountAfterDiscount: 0,
                    itemCount: 0,
                    CGST: 0,
                    SGST: 0,
                    IGST: 0,
                    VAT: 0,
                    totalTax: 0
                };
            }

            taxGroups[taxRate].taxableAmountBeforeDiscount += itemTaxableAmount;
            taxGroups[taxRate].itemCount += 1;
            totalTaxableBeforeDiscount += itemTaxableAmount;
        });

        // Second pass: distribute bill discount across groups, and
        //              recalculate taxes on taxableAmountAfterDiscount
        return Object.values(taxGroups)
            .filter(group => group.taxRate >= 0)
            .sort((a, b) => a.taxRate - b.taxRate)
            .map(group => {
                const proportion = totalTaxableBeforeDiscount > 0 ? group.taxableAmountBeforeDiscount / totalTaxableBeforeDiscount : 0;

                // Distribute discount only to taxable value
                const groupDiscountAmount = (totals.discount || 0) * proportion;
                const netGroupTaxable = Math.max(0, group.taxableAmountBeforeDiscount - groupDiscountAmount);

                let groupTax = 0;
                let groupCGST = 0;
                let groupSGST = 0;
                let groupIGST = 0;
                let groupVAT = 0;

                if (posTaxType === "gst") {
                    if (isSameState) {
                        groupTax = (netGroupTaxable * group.taxRate) / 100;
                        groupCGST = groupTax / 2;
                        groupSGST = groupTax / 2;
                    } else {
                        groupTax = (netGroupTaxable * group.taxRate) / 100;
                        groupIGST = groupTax;
                    }
                } else if (posTaxType === "vat") {
                    groupVAT = (netGroupTaxable * group.taxRate) / 100;
                    groupTax = groupVAT;
                }

                return {
                    ...group,
                    taxableAmountBeforeDiscount: group.taxableAmountBeforeDiscount,
                    taxableAmountAfterDiscount: netGroupTaxable,
                    taxableAmount: netGroupTaxable, // used for table display
                    netTaxableAmount: netGroupTaxable,
                    CGST: groupCGST,
                    SGST: groupSGST,
                    IGST: groupIGST,
                    VAT: groupVAT,
                    totalTax: groupTax,
                    totalAmount: netGroupTaxable + groupTax
                };
            });
    };

    const taxBreakdown = getTaxBreakdown();

    // Calculate totals from breakdown
    const totalTaxableBeforeDiscountFromBreakdown = taxBreakdown.reduce(
        (sum, g) => sum + (g.taxableAmountBeforeDiscount || 0),
        0
    );
    const totalTaxableFromBreakdown = taxBreakdown.reduce(
        (sum, g) => sum + (g.taxableAmount || 0),
        0
    );
    const netTaxableFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.netTaxableAmount, 0);
    const totalCGSTFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.CGST, 0);
    const totalSGSTFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.SGST, 0);
    const totalIGSTFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.IGST, 0);
    const totalVATFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.VAT, 0);
    const totalTaxFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.totalTax, 0);
    const totalAmountFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.totalAmount, 0);

    return (
        <div>
            {/* Discount Controls */}
            <Row className="g-2 mb-1 px-1 pb-1 border-bottom">
                {/* <Col md={3}>
                    <Form.Label>{t("Discount Type")}</Form.Label>
                    <Form.Select
                        size="sm"
                        value={discountType}
                        onChange={(e) => onDiscountTypeChange(e.target.value)}
                    >
                        <option value="Fixed">{t("Fixed")}</option>
                        <option value="Percentage">{t("Percentage")} (%)</option>
                    </Form.Select>
                </Col> */}
                <Col md={4} className="d-flex align-items-center gap-2">
                    <Form.Label className="mb-0 text-nowrap">
                        {t("Discount")} {discountType === "Percentage" && "(%)"}
                    </Form.Label>

                    {discountType === "Percentage" ? (
                        <InputGroup size="sm" className="flex-grow-1">
                            <Form.Control
                                type="number"
                                value={discountValue}
                                min={0}
                                max={100}
                                onChange={(e) =>
                                    onDiscountValueChange(
                                        e.target.value > 0 && e.target.value <= 100 ? e.target.value : 0
                                    )
                                }
                                {...disableScrollProps}
                            />
                            <InputGroup.Text>
                                {getCurrencySymbol()}
                                {totals.discount.toFixed(2)}
                            </InputGroup.Text>
                        </InputGroup>
                    ) : (
                        <Form.Control
                            type="number"
                            size="sm"
                            className="flex-grow-1"
                            min={0}
                            value={discountValue}
                            onChange={(e) =>
                                onDiscountValueChange(
                                    e.target.value > 0 && e.target.value <= totals.netTotal
                                        ? e.target.value
                                        : 0
                                )
                            }
                            {...disableScrollProps}
                        />
                    )}
                </Col>
                {/* <Col md={3} className="align-content-end text-end ms-auto">
                    <h6 className="m-0 tw-text-primary">
                        {t("Net Total")}: {getCurrencySymbol()}
                        {totals.netTotal.toFixed(2)}
                    </h6>
                </Col> */}
            </Row>
            <Scrollbars
                autoHide
                style={{ height: "calc(100vh - 520px)" }}
                universal
                renderTrackHorizontal={(props) => <div {...props} style={{ display: "none" }} />}
            >
                {/* Tax Summary Section */}
                {taxBreakdown.length > 0 && totals.tax > 0 && (
                    <div className="mt-2">
                        <div className="row">
                            {/* Tax Breakdown Table */}
                            <div className="col-md-12 col-lg-8">
                                <h6 className="mb-3">{t("Tax Summary")}</h6>
                                <Table bordered bsSize="sm" className="mb-4 table-sm">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="text-center py-1">{t("Tax %")}</th>
                                            <th className="text-end py-1">{t("Taxable value")}</th>
                                            {totals.discount > 0 && (
                                                <th className="text-end py-1">{t("Taxable Amount (Discounted)")}</th>
                                            )}
                                            {posTaxType === "gst" ? (
                                                isSameState ? (
                                                    <>
                                                        <th className="text-end py-1">{t("CGST Amt")}</th>
                                                        <th className="text-end py-1">{t("SGST Amt")}</th>
                                                    </>
                                                ) : (
                                                    <th className="text-end py-1">{t("IGST Amt")}</th>
                                                )
                                            ) : (
                                                <th className="text-end py-1">{t("VAT Amt")}</th>
                                            )}
                                            <th className="text-end py-1">{t("Total Tax")}</th>
                                            <th className="text-end py-1">{t("Total Amount")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {taxBreakdown.map((group, index) => (
                                            <tr key={index} className={index % 2 === 0 ? "table-default" : "table-light"}>
                                                <td className="text-center fw-bold">
                                                    {group.taxRate}%
                                                </td>
                                                <td className="text-end">
                                                    {getCurrencySymbol()}{group.taxableAmountBeforeDiscount.toFixed(2)}
                                                </td>
                                                {totals.discount > 0 && (
                                                    <td className="text-end">
                                                        {getCurrencySymbol()}{group.taxableAmountAfterDiscount.toFixed(2)}
                                                    </td>
                                                )}
                                                {posTaxType === "gst" ? (
                                                    isSameState ? (
                                                        <>
                                                            <td className="text-end text-success">
                                                                {getCurrencySymbol()}{group.CGST.toFixed(2)}
                                                            </td>
                                                            <td className="text-end text-success">
                                                                {getCurrencySymbol()}{group.SGST.toFixed(2)}
                                                            </td>
                                                        </>
                                                    ) : (
                                                        <td className="text-end text-success">
                                                            {getCurrencySymbol()}{group.IGST.toFixed(2)}
                                                        </td>
                                                    )
                                                ) : (
                                                    <td className="text-end text-success">
                                                        {getCurrencySymbol()}{group.VAT.toFixed(2)}
                                                    </td>
                                                )}
                                                <td className="text-end fw-bold text-success">
                                                    {getCurrencySymbol()}{group.totalTax.toFixed(2)}
                                                </td>
                                                <td className="text-end fw-bold">
                                                    {getCurrencySymbol()}{group.totalAmount.toFixed(2)}
                                                </td>
                                            </tr>
                                        ))}
                                        {/* Total Row */}
                                        <tr className="table-primary fw-bold">
                                            <td className="text-center">{t("Total")}</td>
                                            <td className="text-end">
                                                {getCurrencySymbol()}{totalTaxableBeforeDiscountFromBreakdown.toFixed(2)}
                                            </td>
                                            {totals.discount > 0 && (
                                                <td className="text-end">
                                                    {getCurrencySymbol()}{netTaxableFromBreakdown.toFixed(2)}
                                                </td>
                                            )}
                                            {posTaxType === "gst" ? (
                                                isSameState ? (
                                                    <>
                                                        <td className="text-end">
                                                            {getCurrencySymbol()}{totalCGSTFromBreakdown.toFixed(2)}
                                                        </td>
                                                        <td className="text-end">
                                                            {getCurrencySymbol()}{totalSGSTFromBreakdown.toFixed(2)}
                                                        </td>
                                                    </>
                                                ) : (
                                                    <td className="text-end">
                                                        {getCurrencySymbol()}{totalIGSTFromBreakdown.toFixed(2)}
                                                    </td>
                                                )
                                            ) : (
                                                <td className="text-end">
                                                    {getCurrencySymbol()}{totalVATFromBreakdown.toFixed(2)}
                                                </td>
                                            )}
                                            <td className="text-end text-success">
                                                {getCurrencySymbol()}{totalTaxFromBreakdown.toFixed(2)}
                                            </td>
                                            <td className="text-end">
                                                {getCurrencySymbol()}{totalAmountFromBreakdown.toFixed(2)}
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </div>

                            {/* Final Summary */}
                            <div className="col-md-12 col-lg-4">
                                <Table bordered className="table-sm">
                                    <tbody>
                                        <tr>
                                            <td className="text-end">{t("Total Taxable Value")}</td>
                                            <td className="text-end fw-bold">{getCurrencySymbol()}{totalTaxableBeforeDiscountFromBreakdown.toFixed(2)}</td>
                                        </tr>
                                        {totals.discount > 0 && (
                                            <>
                                                <tr>
                                                    <td className="text-end">{t("Discount")}</td>
                                                    <td className="text-end text-danger">-{getCurrencySymbol()}{totals.discount.toFixed(2)}</td>
                                                </tr>
                                                <tr>
                                                    <td className="text-end">{t("Net Taxable Value")}</td>
                                                    <td className="text-end fw-bold">{getCurrencySymbol()}{netTaxableFromBreakdown.toFixed(2)}</td>
                                                </tr>
                                            </>
                                        )}
                                        {/* Tax Additions */}
                                        {posTaxType === "gst" && isSameState && totalCGSTFromBreakdown > 0 && (
                                            <tr>
                                                <td className="text-end">Add: CGST</td>
                                                <td className="text-end text-success">+{getCurrencySymbol()}{totalCGSTFromBreakdown.toFixed(2)}</td>
                                            </tr>
                                        )}
                                        {posTaxType === "gst" && isSameState && totalSGSTFromBreakdown > 0 && (
                                            <tr>
                                                <td className="text-end">Add: SGST</td>
                                                <td className="text-end text-success">+{getCurrencySymbol()}{totalSGSTFromBreakdown.toFixed(2)}</td>
                                            </tr>
                                        )}
                                        {posTaxType === "gst" && !isSameState && totalIGSTFromBreakdown > 0 && (
                                            <tr>
                                                <td className="text-end">Add: IGST</td>
                                                <td className="text-end text-success">+{getCurrencySymbol()}{totalIGSTFromBreakdown.toFixed(2)}</td>
                                            </tr>
                                        )}
                                        {posTaxType === "vat" && totalVATFromBreakdown > 0 && (
                                            <tr>
                                                <td className="text-end">Add: VAT</td>
                                                <td className="text-end text-success">+{getCurrencySymbol()}{totalVATFromBreakdown.toFixed(2)}</td>
                                            </tr>
                                        )}

                                        {/* Grand Total */}
                                        <tr className="table-primary">
                                            <td className="text-end fw-bold">{t("Grand Total")}</td>
                                            <td className="text-end fw-bold fs-6 text-primary">
                                                {getCurrencySymbol()}{totals.netTotal.toFixed(2)}
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    </div>
                )}
            </Scrollbars>
        </div>
    );
};

export default CartSummary;